import is from '@sindresorhus/is';
import { useCallback, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';

import { usePublishJobItemVersion } from '../api/use-publish-job-item-version';
import { useConfirm } from '../providers/ConfirmProvider';
import { useTrackWorkflowTransition } from './analytics/use-track-workflow-transition';
import { useConfirmDialogPreference } from './use-confirm-dialog-preference';
import { getJobTypeTranslation } from './use-review-action';

type ItemType = 'job-level' | 'job-role';

type PublishableItem = {
    readonly id: string;
    readonly parentId: string | null;
    readonly versionInfo?: {
        readonly version?: number;
    } | null;
};

type UsePublishActionProps = {
    readonly architectureId: string;
    readonly item: PublishableItem;
    readonly itemType: ItemType;
};

type UsePublishActionResult = {
    readonly handlePublish: () => Promise<void>;
};

const getVersionId = (version: number | undefined): string => {
    if (is.undefined(version)) {
        throw new Error('Missing job profile version');
    }

    return version.toString();
};

export const usePublishAction = ({ architectureId, item, itemType }: UsePublishActionProps): UsePublishActionResult => {
    const { translate } = useContext(ApplicationContext);
    const { mutateAsync: publishVersion } = usePublishJobItemVersion();
    const { trackWorkflowTransition } = useTrackWorkflowTransition({ architectureId });
    const { dismissDialog, shouldShowDialog } = useConfirmDialogPreference('publish');
    const { confirm } = useConfirm();
    const jobTypeTranslation = getJobTypeTranslation(itemType, translate);

    const handlePublish = useCallback(async (): Promise<void> => {
        const versionId = getVersionId(item.versionInfo?.version);

        if (shouldShowDialog) {
            const result = await confirm({
                description: translate('pages.job-architecture-v2.job-role-publish-description', {
                    jobType: jobTypeTranslation,
                }),
                renderCancelButton: (onCancel) => (
                    <Button variant="tertiary" onClick={onCancel}>
                        {translate('common.cancel')}
                    </Button>
                ),
                renderConfirmButton: (onConfirm) => (
                    <Button variant="primary" onClick={onConfirm}>
                        {translate('pages.job-architecture-v2.job-role-publish-button')}
                    </Button>
                ),
                showDontShowAgainCheckbox: true,
                title: translate('pages.job-architecture-v2.job-role-publish-title', { jobType: jobTypeTranslation }),
                type: 'default',
            });

            if (result.dontShowAgain) {
                dismissDialog();
            }
        }

        await publishVersion({
            architectureId,
            itemId: item.id,
            itemType,
            version: versionId,
        });

        trackWorkflowTransition({
            actionType: 'publish',
            fromStatus: 'reviewed',
            itemId: item.id,
            itemType,
            parentRoleId: itemType === 'job-level' && !is.null(item.parentId) ? item.parentId : undefined,
            toStatus: 'published',
            versionId,
        });
    }, [
        architectureId,
        confirm,
        dismissDialog,
        item.id,
        item.parentId,
        item.versionInfo?.version,
        itemType,
        jobTypeTranslation,
        publishVersion,
        shouldShowDialog,
        trackWorkflowTransition,
        translate,
    ]);

    return { handlePublish };
};
